import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  Divider,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Search,
  Add,
  CloudDownload,
  LocationOn,
  Phone,
  Web,
  GolfCourse,
  ExpandMore,
  BugReport
} from '@mui/icons-material';
import { 
  testGolfCourseAPI, 
  searchGolfCourses, 
  convertAPICourseToInternal,
  getAvailableTees 
} from '../../utils/golfCourseAPI';

export default function GolfCourseAPIIntegration({ onCourseSelected, onClose }) {
  const [searchParams, setSearchParams] = useState({
    name: 'Milham Park',
    city: 'Kalamazoo',
    state: 'MI',
    country: 'United States'
  });
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [debugInfo, setDebugInfo] = useState(null);
  const [selectedTees, setSelectedTees] = useState({}); // Track selected tee for each course

  const handleSearch = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    setSearchResults([]);
    setDebugInfo(null);

    try {
      console.log('Searching for courses with params:', searchParams);
      
      // Build the search query
      const searchQuery = [searchParams.name, searchParams.city, searchParams.state].filter(Boolean).join(' ');
      const endpoint = `https://api.golfcourseapi.com/v1/search?search_query=${encodeURIComponent(searchQuery)}`;
      
      // Capture debug info
      const debug = {
        searchQuery: searchQuery,
        encodedQuery: encodeURIComponent(searchQuery),
        fullUrl: endpoint,
        timestamp: new Date().toISOString(),
        rawResponse: null,
        parsedResponse: null
      };
      
      // First test the API connection
      const testResult = await testGolfCourseAPI(
        searchParams.name, 
        searchParams.city, 
        searchParams.state
      );
      
      // Add raw response to debug
      debug.rawResponse = testResult;
      
      if (testResult.success) {
        setSuccess(`API connection successful! Found ${testResult.data?.courses?.length || 0} course(s).`);
        console.log('API Test Result:', testResult);
        
        debug.parsedResponse = testResult.data;
        
        // If we got data, store both raw API data and converted courses
        if (testResult.data?.courses && Array.isArray(testResult.data.courses) && testResult.data.courses.length > 0) {
          const coursesWithTees = testResult.data.courses.map((apiCourse, idx) => {
            const availableTees = getAvailableTees(apiCourse);
            const convertedCourse = convertAPICourseToInternal(apiCourse, 0);
            return {
              ...convertedCourse,
              rawApiData: apiCourse,
              courseIndex: idx
            };
          });
          setSearchResults(coursesWithTees);
          debug.convertedCourses = coursesWithTees;
          
          // Initialize selected tees (default to first tee for each course)
          const initialTees = {};
          coursesWithTees.forEach((course, idx) => {
            initialTees[idx] = 0;
          });
          setSelectedTees(initialTees);
        } else {
          setError('No courses found in API response');
        }
      } else {
        setError(`API connection failed: ${testResult.error}`);
      }
      
      setDebugInfo(debug);
    } catch (error) {
      console.error('Search error:', error);
      setError(`Search failed: ${error.message}`);
      setDebugInfo({ error: error.message, stack: error.stack });
    } finally {
      setLoading(false);
    }
  };

  const handleCourseSelect = (course) => {
    if (onCourseSelected) {
      // Re-convert the course using the selected tee
      const selectedTeeIndex = selectedTees[course.courseIndex] || 0;
      const finalCourse = convertAPICourseToInternal(course.rawApiData, selectedTeeIndex);
      onCourseSelected(finalCourse);
    }
    if (onClose) {
      onClose();
    }
  };

  const handleTeeChange = (courseIndex, teeIndex) => {
    setSelectedTees(prev => ({
      ...prev,
      [courseIndex]: teeIndex
    }));
    
    // Update the course in search results with the new tee
    setSearchResults(prevResults => 
      prevResults.map((course, idx) => {
        if (idx === courseIndex) {
          return {
            ...convertAPICourseToInternal(course.rawApiData, teeIndex),
            rawApiData: course.rawApiData,
            courseIndex: idx
          };
        }
        return course;
      })
    );
  };

  const handleInputChange = (field, value) => {
    setSearchParams(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <Dialog open={true} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <CloudDownload />
          <Typography variant="h6">Import Golf Course from API</Typography>
        </Box>
      </DialogTitle>
      
      <DialogActions sx={{ px: 3, py: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Button 
          onClick={() => {
            // Call onCourseSelected with null to trigger manual course creation
            if (onCourseSelected) {
              onCourseSelected(null);
            }
            if (onClose) {
              onClose();
            }
          }}
          variant="outlined"
          startIcon={<Add />}
        >
          Add Golf Course Manually
        </Button>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
      </DialogActions>
      
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Search for golf courses using the Golf Course API and import their details.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Course Name"
              value={searchParams.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="e.g., Milham Park Golf Course"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="City"
              value={searchParams.city}
              onChange={(e) => handleInputChange('city', e.target.value)}
              placeholder="e.g., Kalamazoo"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="State"
              value={searchParams.state}
              onChange={(e) => handleInputChange('state', e.target.value)}
              placeholder="e.g., MI"
            />
          </Grid>
        </Grid>

        <Box display="flex" gap={2} mb={3}>
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} /> : <Search />}
            onClick={handleSearch}
            disabled={loading}
          >
            {loading ? 'Searching...' : 'Search Courses'}
          </Button>
          
          <Button
            variant="outlined"
            onClick={() => {
              setSearchParams({
                name: 'Moors Valley Golf Course',
                city: 'Ringwood',
                state: 'UK',
                country: 'United Kingdom'
              });
              setSearchResults([]);
              setError('');
              setSuccess('');
            }}
          >
            Reset to Test Course
          </Button>
        </Box>

        {debugInfo && (
          <Accordion sx={{ mb: 2, bgcolor: '#f5f5f5' }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box display="flex" alignItems="center" gap={1}>
                <BugReport fontSize="small" />
                <Typography variant="subtitle2">Debug Information</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Paper elevation={0} sx={{ p: 2, bgcolor: '#fff', maxHeight: 400, overflow: 'auto' }}>
                <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', fontSize: '11px' }}>
                  <strong>Search Query:</strong> {debugInfo.searchQuery}
                  {'\n'}
                  <strong>Encoded Query:</strong> {debugInfo.encodedQuery}
                  {'\n\n'}
                  <strong>Full URL:</strong>
                  {'\n'}{debugInfo.fullUrl}
                  {'\n\n'}
                  <strong>Timestamp:</strong> {debugInfo.timestamp}
                  {'\n\n'}
                  {debugInfo.convertedCourses && debugInfo.convertedCourses.length > 0 && (
                    <>
                      <strong>Available Tees Found:</strong>
                      {'\n'}
                      {debugInfo.convertedCourses.map((course, idx) => (
                        course.availableTees && course.availableTees.length > 0 ? 
                        `Course ${idx + 1}: ${course.name}\n${course.availableTees.map(tee => 
                          `  - ${tee.name} (${tee.color}): Par ${tee.par}, ${tee.yardage} yds, Rating ${tee.rating}, Slope ${tee.slope}`
                        ).join('\n')}\n\n` : ''
                      )).join('')}
                    </>
                  )}
                  <strong>Full API Response:</strong>
                  {'\n'}{JSON.stringify(debugInfo.parsedResponse, null, 2)}
                </Typography>
              </Paper>
            </AccordionDetails>
          </Accordion>
        )}

        {searchResults.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>
              Search Results ({searchResults.length})
            </Typography>
            
            <Grid container spacing={2}>
              {searchResults.map((course, index) => (
                <Grid item xs={12} key={index}>
                  <Card>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                        <Typography variant="h6">
                          {course.name}
                        </Typography>
                        <Chip 
                          label={`Par ${course.totalPar}`}
                          color="primary"
                          size="small"
                        />
                      </Box>
                      
                      <Box display="flex" flexDirection="column" gap={1}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <LocationOn fontSize="small" color="action" />
                          <Typography variant="body2">
                            {course.address && `${course.address}, `}
                            {course.city && `${course.city}, `}
                            {course.state && `${course.state}, `}
                            {course.country}
                          </Typography>
                        </Box>
                        
                        {/* Tee Selection */}
                        {course.availableTees && course.availableTees.length > 0 && (
                          <Box sx={{ mt: 2, mb: 1 }}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Select Tee Box</InputLabel>
                              <Select
                                value={selectedTees[course.courseIndex] || 0}
                                onChange={(e) => handleTeeChange(course.courseIndex, e.target.value)}
                                label="Select Tee Box"
                              >
                                {course.availableTees.map((tee, teeIdx) => (
                                  <MenuItem key={teeIdx} value={teeIdx}>
                                    {tee.name} ({tee.color}) - Par {tee.par}, {tee.yardage} yds
                                    {tee.rating > 0 && ` - Rating: ${tee.rating.toFixed(1)}`}
                                    {tee.slope > 0 && ` / Slope: ${tee.slope}`}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            
                            {/* Display selected tee info */}
                            <Box sx={{ mt: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                              <Typography variant="caption" display="block">
                                <strong>Selected Tee:</strong> {course.selectedTee} ({course.teeColor})
                              </Typography>
                              <Typography variant="caption" display="block">
                                <strong>Yardage:</strong> {course.totalYardage} yds
                                {course.courseRating > 0 && ` | Rating: ${course.courseRating.toFixed(1)}`}
                                {course.slopeRating > 0 && ` | Slope: ${course.slopeRating}`}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                        
                        {course.phone && (
                          <Box display="flex" alignItems="center" gap={1}>
                            <Phone fontSize="small" color="action" />
                            <Typography variant="body2">{course.phone}</Typography>
                          </Box>
                        )}
                        
                        {course.website && (
                          <Box display="flex" alignItems="center" gap={1}>
                            <Web fontSize="small" color="action" />
                            <Typography variant="body2" color="primary">
                              {course.website}
                            </Typography>
                          </Box>
                        )}
                        
                        {course.architect && (
                          <Typography variant="body2" color="text.secondary">
                            Designed by: {course.architect}
                          </Typography>
                        )}
                        
                        {course.yearOpened && (
                          <Typography variant="body2" color="text.secondary">
                            Opened: {course.yearOpened}
                          </Typography>
                        )}
                      </Box>
                    </CardContent>
                    
                    <CardActions>
                      <Button
                        size="small"
                        startIcon={<Add />}
                        onClick={() => handleCourseSelect(course)}
                        variant="contained"
                      >
                        Import This Course
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
